// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ImgProps } from "@insite/mobius/Img";
// import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LazyImageProps } from "@conklin/MobiusOverrides/LazyImage";
import LazyPicture from "@conklin/MobiusOverrides/LazyImage/LazyPicture";
import { validateCssLength } from "@conklin/XDL/Utilities";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { FC, useRef } from "react";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    altText = "altText",
    imageLink = "imageLink",
    // XNG-Change
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    openInNewTab = "openInNewTab",
    fullWidthImage = "fullWidthImage",
    fullHeightImage = "fullHeightImage",
    disableLazyLoad = "disableLazyLoad",
    borderRadius = "borderRadius",
    contentTitle = "contentTitle",
    contentMessage = "contentMessage",
    contentLink = "contentLink",
    contentLinkLabel = "contentLinkLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        // XNG-Change
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.openInNewTab]: boolean;
        [fields.fullWidthImage]: boolean;
        [fields.fullHeightImage]: boolean;
        [fields.disableLazyLoad]: boolean;
        [fields.borderRadius]: string;
        [fields.contentTitle]: string;
        [fields.contentMessage]: string;
        [fields.contentLink]: LinkFieldValue;
        [fields.contentLinkLabel]: string;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    // XNG-Changes
    contentWrapper?: InjectableCss;
    contentMessage?: TypographyPresentationProps;
    contentLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
};

const styles = imageStyles;

const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);

    // XNG-Changes
    const contentLink = useGetLink(fields.contentLink);
    const ref = useRef<HTMLDivElement>(null);

    const image = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            disableLazyLoad={fields.disableLazyLoad}
            className="img"
        />
    );

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}

            // XNG-Changes             
            ${fields.borderRadius !== "" &&
            css`
                img {
                    border-radius: ${fields.borderRadius};
                }
            `}

            // XNG-Changes             
            ${(!!fields.contentTitle || !!fields.contentMessage) &&
            css`
                position: relative;
            `}
            // XNG-Changes             
            ${!!fields.fullHeightImage &&
            css`
                height: 100%;
            `}
            // XNG-Changes             
            ${!!fields.fullWidthImage &&
            css`
                width: 100%;
            `}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper
                className="img-wrapper"
                {...styles.wrapper}
                // XNG-Changes
                {...{
                    fullWidthImage: fields.fullWidthImage,
                    fullHeightImage: fields.fullHeightImage,
                    maxHeight: ref.current?.clientHeight,
                }}
            >
                {url ? (
                    <Link
                        className="img-link"
                        href={url}
                        target={fields.openInNewTab ? "_blank" : "_self"}
                        {...styles.imageLink}
                        aria-label={`${fields.altText}`}
                    >
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
            {(!!fields.contentTitle || !!fields.contentMessage) && (
                <StyledWrapper
                    {...styles.contentWrapper}
                    // XNG-Changes
                    ref={ref}
                >
                    {!!fields.contentTitle && (
                        <Typography variant="h2" color="common.ui1">
                            {fields.contentTitle}
                        </Typography>
                    )}
                    {!!fields.contentMessage && (
                        <Typography {...styles.contentMessage}>{fields.contentMessage}</Typography>
                    )}
                    {contentLink.url && (
                        <Link href={contentLink.url} {...styles.contentLink}>
                            {fields.contentLinkLabel || contentLink.title}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const contentTab = {
    displayName: "Content",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            // XNG-Changes
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            // XNG-Changes
            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            // XNG-Changes
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.openInNewTab,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.fullWidthImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.fullHeightImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.disableLazyLoad,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            // XNG-Changes
            {
                name: fields.borderRadius,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
                validate: validateCssLength,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.contentTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: fields.contentMessage,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: fields.contentLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: contentTab,
            },
            {
                name: fields.contentLinkLabel,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
        ],
    },
};

export default widgetModule;
